import React, { useState, useEffect } from 'react';
import './Products.css';
import ConfirmDialog from '../common/ConfirmDialog';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
const MAX_VIDEO_SIZE = 20 * 1024 * 1024;
const ALLOWED_VIDEO_TYPES = ['video/mp4', 'video/webm'];
const LIMIT = 50;

const Products = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  const [searchTerm, setSearchTerm] = useState('');
  const [filterBrand, setFilterBrand] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [sortBy, setSortBy] = useState('newest');

  const [selectedProducts, setSelectedProducts] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);

  // Edit modal state
  const [editModal, setEditModal] = useState(null);
  const [editTab, setEditTab] = useState('details');
  const [editLoading, setEditLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editForm, setEditForm] = useState({ title: '', description: '' });
  const [variantEdits, setVariantEdits] = useState({});
  const [heroUploading, setHeroUploading] = useState(false);
  const [heroVideoUploading, setHeroVideoUploading] = useState(false);
  const [deletingHeroVideo, setDeletingHeroVideo] = useState(false);
  const [variantUploading, setVariantUploading] = useState(null);
  const [deletingImageId, setDeletingImageId] = useState(null);

  // Add Product modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [addSaving, setAddSaving] = useState(false);
  const [addForm, setAddForm] = useState({
    title: '', description: '', vendor_name: '', category_id: '',
  });
  const [addVariants, setAddVariants] = useState([
    { sku: '', attribute: '', size: '', retail_price: '', wholesale_price: '', cost_price: '', case_qty: '', inventory_qty: '0' }
  ]);
  const [categoryList, setCategoryList] = useState([]);
  const [addHeroImage, setAddHeroImage] = useState(null);
  const [addHeroVideo, setAddHeroVideo] = useState(null);
  const [addHeroPreview, setAddHeroPreview] = useState(null);
  const [addHeroVideoPreview, setAddHeroVideoPreview] = useState(null);
  const [addVariantImages, setAddVariantImages] = useState({});
  const [brandList, setBrandList] = useState([]);
  const [showNewBrandInput, setShowNewBrandInput] = useState(false);
  const [newBrandName, setNewBrandName] = useState('');

  const getImageUrl = (localPath) => {
    if (!localPath) return '/images/empty_cart.webp';
    if (localPath.startsWith('http')) return localPath;
    if (localPath.startsWith('/uploads/')) {
      const baseUrl = API_URL.replace('/api', '');
      return `${baseUrl}${localPath}`;
    }
    const baseUrl = API_URL.replace('/api', '');
    return `${baseUrl}/api/images/${localPath}`;
  };

  useEffect(() => {
    fetchProducts({ page: 1 });
    fetchCategories();
    fetchBrands();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1);
      fetchProducts({
        search: searchTerm,
        brand: filterBrand,
        category: filterCategory,
        sort: sortBy,
        page: 1
      });
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm, filterBrand, filterCategory, sortBy]);

  useEffect(() => {
    if (currentPage > 1) {
      fetchProducts({
        search: searchTerm,
        brand: filterBrand,
        category: filterCategory,
        sort: sortBy,
        page: currentPage
      });
    }
  }, [currentPage]);

  const fetchBrands = async () => {
    try {
      const res = await fetch(`${API_URL}/brands`);
      const data = await res.json();
      if (data.success) setBrandList(data.data || []);
    } catch (err) { console.error('Error fetching brands:', err); }
  };

  const fetchCategories = async () => {
    try {
      const res = await fetch(`${API_URL}/product-groups/categories`);
      const data = await res.json();
      if (data.success) {
        const flat = [];
        for (const parent of (data.data || [])) {
          flat.push({ 
            id: parent.id, 
            name: parent.name, 
            slug: parent.slug,  // thêm dòng này
            parent_name: null 
          });
          for (const child of (parent.children || [])) {
            flat.push({ 
              id: child.id, 
              name: child.name, 
              slug: child.slug,  // thêm dòng này
              parent_name: parent.name 
            });
          }
        }
        setCategoryList(flat);
      }
    } catch (err) { console.error('Error fetching categories:', err); }
  };

  const fetchProducts = async (params) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const offset = ((params?.page || currentPage) - 1) * LIMIT;

      const url = new URL(`${API_URL}/product-groups/admin/all`);
      url.searchParams.set('limit', LIMIT.toString());
      url.searchParams.set('offset', offset.toString());

      if (params?.search) url.searchParams.set('search', params.search);
      if (params?.brand && params.brand!== 'all') url.searchParams.set('vendor', params.brand);
      if (params?.category && params.category!== 'all') url.searchParams.set('category', params.category);
      if (params?.sort) url.searchParams.set('sort', params.sort);

      const res = await fetch(url.toString(), {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.status === 401) {
        setError('Session expired. Please login again.');
        return;
      }

      const data = await res.json();
      if (data.success) {
        setProducts(data.data);
        setTotalCount(data.total || 0);
        setError(null);
      } else {
        setProducts([]);
        setError('Failed to load products');
      }
    } catch (err) {
      console.error('Error fetching products:', err);
      setProducts([]);
      setError('Error connecting to server.');
    } finally {
      setLoading(false);
    }
  };

  const refetchCurrent = () => {
    fetchProducts({
      search: searchTerm,
      brand: filterBrand,
      category: filterCategory,
      sort: sortBy,
      page: currentPage
    });
  };

  const openAddModal = () => {
    setAddForm({ title: '', description: '', vendor_name: '', category_id: '' });
    setAddVariants([{ sku: '', attribute: '', size: '', retail_price: '', wholesale_price: '', cost_price: '', case_qty: '', inventory_qty: '0' }]);
    setAddHeroImage(null);
    setAddHeroVideo(null);
    setAddHeroPreview(null);
    setAddHeroVideoPreview(null);
    setAddVariantImages({});
    setShowNewBrandInput(false);
    setNewBrandName('');
    setShowAddModal(true);
  };

  const addVariantRow = () => {
    setAddVariants(prev => [...prev, { sku: '', attribute: '', size: '', retail_price: '', wholesale_price: '', cost_price: '', case_price: '', case_qty: '', inventory_qty: '0' }]);
  };

  const removeVariantRow = (idx) => {
    if (addVariants.length <= 1) return;
    setAddVariants(prev => prev.filter((_, i) => i!== idx));
    setAddVariantImages(prev => {
      const n = {...prev };
      delete n[idx];
      const reindexed = {};
      Object.entries(n).forEach(([k, v]) => {
        const ki = parseInt(k);
        reindexed[ki > idx? ki - 1 : ki] = v;
      });
      return reindexed;
    });
  };

  const updateAddVariant = (idx, field, value) => {
    setAddVariants(prev => prev.map((v, i) => i === idx? {...v, [field]: value } : v));
  };

  const submitAddProduct = async () => {
    if (!addForm.title.trim()) return alert('Product name is required');
    const validVariants = addVariants.filter(v => v.retail_price);
    if (validVariants.length === 0) return alert('At least one variant with a retail price is required');
    setAddSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/product-groups/admin/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          title: addForm.title,
          description: addForm.description,
          vendor_name: addForm.vendor_name || null,
          category_id: addForm.category_id? parseInt(addForm.category_id) : null,
          variants: validVariants.map(v => ({
            sku: v.sku || null,
            attribute: v.attribute || null,
            size: v.size || null,
            retail_price: parseFloat(v.retail_price),
            wholesale_price: v.wholesale_price? parseFloat(v.wholesale_price) : null,
            cost_price: v.cost_price? parseFloat(v.cost_price) : null,
            case_price: v.case_price? parseFloat(v.case_price) : null,
            case_qty: v.case_qty? parseInt(v.case_qty) : null,
            inventory_qty: parseInt(v.inventory_qty) || 0,
          }))
        })
      });
      const data = await res.json();
      if (data.success) {
        const groupId = data.data.id;
        const createdVariants = data.data.variants || [];

        if (addHeroImage && groupId) {
          const formData = new FormData();
          formData.append('image', addHeroImage);
          await fetch(`${API_URL}/product-groups/admin/${groupId}/hero-image`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` },
            body: formData
          });
        }

        if (addHeroVideo && groupId) {
          const formData = new FormData();
          formData.append('video', addHeroVideo);
          await fetch(`${API_URL}/product-groups/admin/${groupId}/hero-video`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` },
            body: formData
          });
        }

        for (const [idxStr, file] of Object.entries(addVariantImages)) {
          const idx = parseInt(idxStr);
          const variant = createdVariants[idx];
          if (variant && file) {
            const formData = new FormData();
            formData.append('image', file);
            await fetch(`${API_URL}/product-groups/admin/variant/${variant.id}/image`, {
              method: 'POST',
              headers: { 'Authorization': `Bearer ${token}` },
              body: formData
            });
          }
        }
        setShowAddModal(false);
        refetchCurrent();
      } else {
        alert(data.error || 'Failed to create product');
      }
    } catch (err) {
      console.error('Error creating product:', err);
      alert('Error creating product');
    } finally {
      setAddSaving(false);
    }
  };

  const openEditModal = async (product) => {
    setEditTab('details');
    setEditLoading(true);
    setEditModal(product);
    setEditForm({ title: product.title || '', description: product.description || '' });
    setVariantEdits({});
    try {
      const res = await fetch(`${API_URL}/product-groups/${product.group_id}`);
      const data = await res.json();
      if (data.success) {
        setEditModal({...product,...data.data, variants: data.data.preview_variants || [] });
      }
    } catch (err) {
      console.error('Error loading product details:', err);
    } finally {
      setEditLoading(false);
    }
  };

  const closeEditModal = () => {
    setEditModal(null);
    setVariantEdits({});
  };

  const saveDetails = async () => {
    if (!editModal) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/product-groups/admin/${editModal.group_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ title: editForm.title, description: editForm.description })
      });
      refetchCurrent();
      setEditModal(prev => prev? {...prev, title: editForm.title, description: editForm.description } : null);
    } catch (err) {
      console.error('Error saving details:', err);
    } finally {
      setSaving(false);
    }
  };

  const getVariantEdit = (variantId) => variantEdits[variantId] || {};

  const setVariantField = (variantId, field, value) => {
    setVariantEdits(prev => ({
     ...prev,
      [variantId]: {...prev[variantId], [field]: value }
    }));
  };

  const saveVariantPricing = async (variant) => {
    const edits = variantEdits[variant.id];
    if (!edits || Object.keys(edits).length === 0) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/product-groups/admin/variant/${variant.id}/pricing`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(edits)
      });
      setEditModal(prev => {
        if (!prev) return null;
        const updatedVariants = (prev.variants || []).map(v =>
          v.id === variant.id? {...v,...edits } : v
        );
        return {...prev, variants: updatedVariants };
      });
      setVariantEdits(prev => { const n = {...prev }; delete n[variant.id]; return n; });
      refetchCurrent();
    } catch (err) {
      console.error('Error saving variant pricing:', err);
    } finally {
      setSaving(false);
    }
  };

  const uploadHeroVideo = async (e) => {
    const file = e.target.files?.[0];
    if (!file ||!editModal) return;

    if (!ALLOWED_VIDEO_TYPES.includes(file.type)) {
      alert('Only.mp4 or.webm files are allowed');
      e.target.value = '';
      return;
    }
    if (file.size > MAX_VIDEO_SIZE) {
      alert(`Video is too large. Max ${MAX_VIDEO_SIZE / 1024 / 1024}MB`);
      e.target.value = '';
      return;
    }

    setHeroVideoUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('video', file);
      const res = await fetch(`${API_URL}/product-groups/admin/${editModal.group_id}/hero-video`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      const data = await res.json();
      if (data.success) {
        setEditModal(prev => prev? {...prev, hero_video: data.videoUrl } : null);
        refetchCurrent();
      } else {
        alert(data.error || 'Upload failed');
      }
    } catch (err) {
      console.error('Error uploading hero video:', err);
      alert('Error uploading video');
    } finally {
      setHeroVideoUploading(false);
      e.target.value = '';
    }
  };

  const deleteHeroVideo = async () => {
    if (!editModal) return;
    if (!window.confirm('Delete hero video?')) return;

    setDeletingHeroVideo(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/product-groups/admin/${editModal.group_id}/hero-video`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setEditModal(prev => prev? {...prev, hero_video: null } : null);
        refetchCurrent();
      } else {
        alert(data.error || 'Failed to delete video');
      }
    } catch (err) {
      console.error('Error deleting hero video:', err);
      alert('Error deleting video');
    } finally {
      setDeletingHeroVideo(false);
    }
  };

  const uploadHeroImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file ||!editModal) return;
    setHeroUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('image', file);
      const res = await fetch(`${API_URL}/product-groups/admin/${editModal.group_id}/hero-image`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      const data = await res.json();
      if (data.success) {
        setEditModal(prev => prev? {...prev, hero_image: data.imageUrl } : null);
        refetchCurrent();
      }
    } catch (err) {
      console.error('Error uploading hero image:', err);
    } finally {
      setHeroUploading(false);
      e.target.value = '';
    }
  };

  const uploadVariantImage = async (variantId, e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setVariantUploading(variantId);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      for (let i = 0; i < files.length; i++) {
        formData.append('images', files[i]);
      }
      const res = await fetch(`${API_URL}/product-groups/admin/variant/${variantId}/images`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      const data = await res.json();
      if (data.success) {
        const detailRes = await fetch(`${API_URL}/product-groups/${editModal.group_id}`);
        const detailData = await detailRes.json();
        if (detailData.success) {
          setEditModal(prev => prev? {...prev, variants: detailData.data.preview_variants || [] } : null);
        }
      }
    } catch (err) {
      console.error('Error uploading variant images:', err);
    } finally {
      setVariantUploading(null);
      e.target.value = '';
    }
  };

  const deleteVariantImage = async (imageId) => {
    setDeletingImageId(imageId);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/product-groups/admin/variant-image/${imageId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setEditModal(prev => {
          if (!prev) return null;
          const updatedVariants = (prev.variants || []).map(v => ({
           ...v,
            images: (v.images || []).filter(img => img.id!== imageId)
          }));
          return {...prev, variants: updatedVariants };
        });
      }
    } catch (err) {
      console.error('Error deleting variant image:', err);
    } finally {
      setDeletingImageId(null);
    }
  };

  const handleDelete = async (groupId) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/product-groups/admin/${groupId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ is_active: false })
      });
      refetchCurrent();
      setShowDeleteConfirm(false);
      setProductToDelete(null);
    } catch (err) {
      console.error('Error deleting product:', err);
    }
  };

  const toggleSelectProduct = (id) => {
    setSelectedProducts(prev =>
      prev.includes(id)? prev.filter(p => p!== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedProducts.length === products.length && products.length > 0) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(products.map(p => p.group_id));
    }
  };

  const confirmBulkDelete = async () => {
    setBulkDeleteConfirm(false);
    try {
      const token = localStorage.getItem('token');
      await Promise.all(selectedProducts.map(id =>
        fetch(`${API_URL}/product-groups/admin/${id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ is_active: false })
        })
      ));
      refetchCurrent();
      setSelectedProducts([]);
    } catch (err) {
      console.error('Error bulk deleting:', err);
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilterBrand('all');
    setFilterCategory('all');
  };

  const activeFiltersCount = [searchTerm!== '', filterBrand!== 'all', filterCategory!== 'all'].filter(Boolean).length;
  const totalPages = Math.ceil(totalCount / LIMIT);

  if (loading && products.length === 0) return <div className="products-loading">Loading products...</div>;
  if (error) return (
    <div className="products-error">
      <p>{error}</p>
      <button onClick={refetchCurrent} className="btn-retry">Retry</button>
    </div>
  );

  return (
    <div className="products-management">
      <div className="products-header">
        <div className="products-controls">
          <input type="text" placeholder="Search products..." value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)} className="search-input" />
          <select value={filterBrand} onChange={(e) => setFilterBrand(e.target.value)} className="filter-select">
            <option value="all">All Brands</option>
            {brandList
            .slice()
            .sort((a, b) => a.name.localeCompare(b.name))
            .map(brand => (
                <option key={brand.id} value={brand.name}>{brand.name}</option>
              ))}
          </select>

          <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)} className="filter-select">
            <option value="all">All Categories</option>
            {categoryList
            .slice()
            .sort((a, b) => {
                const aName = a.parent_name? `${a.parent_name} › ${a.name}` : a.name;
                const bName = b.parent_name? `${b.parent_name} › ${b.name}` : b.name;
                return aName.localeCompare(bName);
              })
            .map(cat => (
                <option key={cat.id} value={cat.slug}>
                  {cat.parent_name? `${cat.parent_name} › ` : ''}{cat.name}
                </option>
              ))}
          </select>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="sort-select">
            <option value="newest">Newest</option>
            <option value="name">Sort by Name</option>
            <option value="priceAsc">Price: Low to High</option>
            <option value="priceDesc">Price: High to Low</option>
            <option value="featured">Featured</option>
          </select>
          {activeFiltersCount > 0 && (
            <button className="btn-clear-filters" onClick={clearFilters}>Clear Filters ({activeFiltersCount})</button>
          )}
        </div>
      </div>

      {selectedProducts.length > 0 && (
        <div className="bulk-actions">
          <span>{selectedProducts.length} selected</span>
          <button onClick={() => setBulkDeleteConfirm(true)} className="btn-bulk-delete">Delete Selected</button>
        </div>
      )}

      <div className="products-info-bar">
        <span className="products-count">
          Showing {products.length} of {totalCount} product groups
        </span>
        <div className="pagination">
          <button disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>Prev</button>
          <span>Page {currentPage} / {totalPages || 1}</span>
          <button disabled={currentPage >= totalPages} onClick={() => setCurrentPage(p => p + 1)}>Next</button>
        </div>
        <div className="buttons-group">
          <button className="btn-add-product" onClick={openAddModal}>+ Add Product</button>
          <button className="btn-add-product" onClick={() => window.open('/management/create-product-combo', '_blank')}>+ Add Combo Product</button>
          <button className="btn-add-product" onClick={() => window.open('/management/product-import', '_blank')}>+ Import Product</button>
        </div>
      </div>

      <div className="products-table-container">
        <table className="products-table">
          <thead>
            <tr>
              <th><input type="checkbox" checked={selectedProducts.length === products.length && products.length > 0} onChange={toggleSelectAll} /></th>
              <th>Image</th>
              <th>Name</th>
              <th>Brand</th>
              <th>Category</th>
              <th>Price</th>
              <th>Variants</th>
              <th>Stock</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {products.map(product => (
              <tr key={product.group_id}>
                <td><input type="checkbox" checked={selectedProducts.includes(product.group_id)} onChange={() => toggleSelectProduct(product.group_id)} /></td>
                <td>
                  <img src={getImageUrl(product.hero_image)} alt={product.title} className="product-thumbnail"
                    onError={(e) => { e.target.src = '/images/empty_cart.webp'; }} />
                </td>
                <td><span className="editable-cell" onClick={() => openEditModal(product)}>{product.title}</span></td>
                <td>{product.brand_name || product.vendor_name || '-'}</td>
                <td>{product.category_name? `${product.parent_category_name? product.parent_category_name + ' › ' : ''}${product.category_name}` : '-'}</td>
                <td>
                  ${parseFloat(product.min_price).toFixed(2)}
                  {product.max_price && parseFloat(product.max_price)!== parseFloat(product.min_price) && (
                    <span className="price-range"> - ${parseFloat(product.max_price).toFixed(2)}</span>
                  )}
                </td>
                <td><span className={`variant-badge ${parseInt(product.total_variants) > 1? 'multi' : ''}`}>{product.total_variants}</span></td>
                <td><span className={`status-badge ${product.in_stock? 'active' : 'inactive'}`}>{product.in_stock? `${product.total_stock}` : 'Out'}</span></td>
                <td>
                  <div className="action-buttons">
                    <button className="btn-edit" onClick={() => openEditModal(product)}>Edit</button>
                    <button className="btn-delete" onClick={() => { setProductToDelete(product); setShowDeleteConfirm(true); }}>Delete</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editModal && (
        <div className="modal-overlay" onClick={closeEditModal}>
          <div className="edit-product-modal" onClick={(e) => e.stopPropagation()}>
            <div className="edit-modal-header">
              <h3>{editModal.title}</h3>
              <button className="btn-close-modal" onClick={closeEditModal}>✕</button>
            </div>

            <div className="edit-modal-tabs">
              <button className={`edit-tab ${editTab === 'details'? 'active' : ''}`} onClick={() => setEditTab('details')}>Details</button>
              <button className={`edit-tab ${editTab === 'pricing'? 'active' : ''}`} onClick={() => setEditTab('pricing')}>Pricing</button>
              <button className={`edit-tab ${editTab === 'images'? 'active' : ''}`} onClick={() => setEditTab('images')}>Images</button>
              <button className={`edit-tab ${editTab === 'video'? 'active' : ''}`} onClick={() => setEditTab('video')}>Video</button>
            </div>

            <div className="edit-modal-body">
              {editLoading? (
                <div className="edit-loading">Loading product details...</div>
              ) : editTab === 'details'? (
                <div className="edit-details-tab">
                  <div className="form-section">
                    <label className="form-label">Product Name</label>
                    <input type="text" className="form-input" value={editForm.title}
                      onChange={(e) => setEditForm(f => ({...f, title: e.target.value }))} />
                  </div>
                  <div className="form-section">
                    <label className="form-label">Description</label>
                    <textarea className="form-textarea" rows={5} value={editForm.description}
                      onChange={(e) => setEditForm(f => ({...f, description: e.target.value }))}
                      placeholder="Enter product description..." />
                  </div>
                  <div className="edit-detail-info">
                    <div><span className="detail-label">Brand:</span> {editModal.brand_name || editModal.vendor_name || '—'}</div>
                    <div><span className="detail-label">Category:</span> {editModal.parent_category_name? `${editModal.parent_category_name} › ` : ''}{editModal.category_name || '—'}</div>
                    <div><span className="detail-label">Variants:</span> {(editModal.variants || []).length}</div>
                  </div>
                  <div className="edit-actions">
                    <button className="btn-save-details" onClick={saveDetails} disabled={saving}>
                      {saving? 'Saving...' : 'Save Details'}
                    </button>
                  </div>
                </div>
              ) : editTab === 'pricing'? (
                <div className="edit-pricing-tab">
                  {(editModal.variants || []).length === 0? (
                    <div className="no-variants">No variants found</div>
                  ) : (
                    <div className="variants-pricing-list">
                      {(editModal.variants || []).map(variant => {
                        const edits = getVariantEdit(variant.id);
                        const hasEdits = Object.keys(edits).length > 0;
                        return (
                          <div key={variant.id} className="variant-pricing-card">
                            <div className="variant-pricing-header">
                              <span className="variant-name">{variant.attribute || variant.size || variant.sku || `Variant #${variant.id}`}</span>
                              {variant.sku && <span className="variant-sku">SKU: {variant.sku}</span>}
                            </div>
                            <div className="variant-pricing-grid">
                              <div className="pricing-field">
                                <label>Retail Price</label>
                                <input type="number" step="0.01"
                                  value={edits.retail_price!== undefined? edits.retail_price : (variant.retail_price || '')}
                                  onChange={(e) => setVariantField(variant.id, 'retail_price', e.target.value)} />
                              </div>
                              <div className="pricing-field">
                                <label>Wholesale Price</label>
                                <input type="number" step="0.01"
                                  value={edits.wholesale_price!== undefined? edits.wholesale_price : (variant.wholesale_price || '')}
                                  onChange={(e) => setVariantField(variant.id, 'wholesale_price', e.target.value)} />
                              </div>
                              <div className="pricing-field">
                                <label>Cost Price</label>
                                <input type="number" step="0.01"
                                  value={edits.cost_price!== undefined? edits.cost_price : (variant.cost_price || '')}
                                  onChange={(e) => setVariantField(variant.id, 'cost_price', e.target.value)} />
                              </div>
                              <div className="pricing-field">
                                <label>Case Price</label>
                                <input type="number" step="0.01"
                                  value={edits.case_price!== undefined? edits.case_price : (variant.case_price || '')}
                                  onChange={(e) => setVariantField(variant.id, 'case_price', e.target.value)} />
                              </div>
                              <div className="pricing-field">
                                <label>Case Qty</label>
                                <input type="number" step="1"
                                  value={edits.case_qty!== undefined? edits.case_qty : (variant.case_qty || '')}
                                  onChange={(e) => setVariantField(variant.id, 'case_qty', e.target.value)} />
                              </div>
                            </div>
                            {hasEdits && (
                              <div className="variant-pricing-actions">
                                <button className="btn-save-variant" onClick={() => saveVariantPricing(variant)} disabled={saving}>
                                  {saving? 'Saving...' : 'Save'}
                                </button>
                                <button className="btn-cancel-variant" onClick={() => setVariantEdits(prev => { const n = {...prev }; delete n[variant.id]; return n; })}>
                                  Cancel
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ) : editTab === 'images'? (
                <div className="edit-images-tab">
                  <div className="hero-image-section">
                    <h4>Hero Image</h4>
                    <div className="hero-image-preview">
                      <img src={getImageUrl(editModal.hero_image)} alt="Hero"
                        onError={(e) => { e.target.src = '/images/empty_cart.webp'; }} />
                      <label className="btn-change-image">
                        {heroUploading? 'Uploading...' : 'Change Image'}
                        <input type="file" accept="image/*" onChange={uploadHeroImage} hidden disabled={heroUploading} />
                      </label>
                    </div>
                  </div>
                  <div className="variant-images-section">
                    <h4>Variant Images</h4>
                    <div className="variant-images-list">
                      {(editModal.variants || []).map(variant => (
                        <div key={variant.id} className="variant-images-block">
                          <div className="variant-images-block-header">
                            <span className="variant-images-label">{variant.attribute || variant.size || variant.sku || `#${variant.id}`}</span>
                            <label className="btn-add-variant-images">
                              {variantUploading === variant.id? 'Uploading...' : '+ Add Images'}
                              <input type="file" accept="image/*" multiple onChange={(e) => uploadVariantImage(variant.id, e)} hidden
                                disabled={variantUploading === variant.id} />
                            </label>
                          </div>
                          <div className="variant-multi-images-grid">
                            {(!variant.images || variant.images.length === 0) && variant.image_url && (
                              <div className="variant-multi-image-card">
                                <img src={getImageUrl(variant.image_url)} alt={variant.attribute || variant.sku}
                                  onError={(e) => { e.target.src = '/images/empty_cart.webp'; }} />
                                <span className="variant-img-badge">Primary</span>
                              </div>
                            )}
                            {(variant.images || []).map((img, imgIdx) => (
                              <div key={img.id} className="variant-multi-image-card">
                                <img src={getImageUrl(img.image_url)} alt={`${variant.attribute || ''} ${imgIdx + 1}`}
                                  onError={(e) => { e.target.src = '/images/empty_cart.webp'; }} />
                                {imgIdx === 0 && <span className="variant-img-badge">Primary</span>}
                                <button className="btn-delete-variant-img" onClick={() => deleteVariantImage(img.id)}
                                  disabled={deletingImageId === img.id}>
                                  {deletingImageId === img.id? '...' : '✕'}
                                </button>
                              </div>
                            ))}
                                                        {(!variant.images || variant.images.length === 0) &&!variant.image_url && (
                              <div className="variant-no-images">No images yet</div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : editTab === 'video'? (
                <div className="edit-video-tab">
                  <div className="hero-video-section">
                    <h4>Hero Video</h4>
                    <div className="hero-video-preview">
                      {editModal.hero_video? (
                        <>
                          <video src={getImageUrl(editModal.hero_video)} controls className="hero-video" />
                          <div className="video-actions">
                            <label className="btn-change-image">
                              {heroVideoUploading? 'Uploading...' : 'Change Video'}
                              <input
                                type="file"
                                accept="video/mp4,video/webm"
                                onChange={uploadHeroVideo}
                                hidden
                                disabled={heroVideoUploading || deletingHeroVideo}
                              />
                            </label>
                            <button
                              className="btn-delete-video"
                              onClick={deleteHeroVideo}
                              disabled={heroVideoUploading || deletingHeroVideo}
                            >
                              {deletingHeroVideo? 'Deleting...' : 'Delete Video'}
                            </button>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="no-video">No video yet</div>
                          <label className="btn-change-image">
                            {heroVideoUploading? 'Uploading...' : 'Upload Video'}
                            <input
                              type="file"
                              accept="video/mp4,video/webm"
                              onChange={uploadHeroVideo}
                              hidden
                              disabled={heroVideoUploading}
                            />
                          </label>
                        </>
                      )}
                    </div>
                    <div className="upload-hint">MP4/WebM, max 20MB, 15-30s</div>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirm && productToDelete && (
        <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Delete Product Group</h3>
            <p>Are you sure you want to delete "{productToDelete.title}"?</p>
            <div className="modal-actions">
              <button className="btn-cancel" onClick={() => setShowDeleteConfirm(false)}>Cancel</button>
              <button className="btn-confirm-delete" onClick={() => handleDelete(productToDelete.group_id)}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="edit-product-modal add-product-modal" onClick={(e) => e.stopPropagation()}>
            <div className="edit-modal-header">
              <h3>Add New Product</h3>
              <button className="btn-close-modal" onClick={() => setShowAddModal(false)}>✕</button>
            </div>
            <div className="edit-modal-body">
              <div className="form-section">
                <label className="form-label">Product Name *</label>
                <input type="text" className="form-input" value={addForm.title}
                  onChange={(e) => setAddForm(f => ({...f, title: e.target.value }))} placeholder="e.g. SNS Dipping Powder" />
              </div>
              <div className="form-section">
                <label className="form-label">Description</label>
                <textarea className="form-textarea" rows={3} value={addForm.description}
                  onChange={(e) => setAddForm(f => ({...f, description: e.target.value }))} placeholder="Product description..." />
              </div>
              <div className="form-row">
                <div className="form-section">
                  <label className="form-label">Brand</label>
                  {showNewBrandInput? (
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <input type="text" className="form-input" value={newBrandName}
                        onChange={(e) => setNewBrandName(e.target.value)} placeholder="New brand name..." />
                      <button type="button" className="btn-add-brand" onClick={async () => {
                        if (!newBrandName.trim()) return;
                        try {
                          const res = await fetch(`${API_URL}/brands`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ name: newBrandName.trim() }),
                          });
                          const data = await res.json();
                          if (data.success) {
                            await fetchBrands();
                            setAddForm(f => ({...f, vendor_name: newBrandName.trim() }));
                            setNewBrandName('');
                            setShowNewBrandInput(false);
                          }
                        } catch (err) { console.error(err); }
                      }}>Add</button>
                      <button type="button" className="btn-cancel-brand" onClick={() => { setShowNewBrandInput(false); setNewBrandName(''); }}>✕</button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <select className="form-input" value={addForm.vendor_name}
                        onChange={(e) => setAddForm(f => ({...f, vendor_name: e.target.value }))}>
                        <option value="">Select brand...</option>
                        {brandList.map(b => <option key={b.id} value={b.name}>{b.name}</option>)}
                      </select>
                      <button type="button" className="btn-new-brand" onClick={() => setShowNewBrandInput(true)}>+ New</button>
                    </div>
                  )}
                </div>
                <div className="form-section">
                  <label className="form-label">Category</label>
                  <select className="form-input" value={addForm.category_id}
                    onChange={(e) => setAddForm(f => ({...f, category_id: e.target.value }))}>
                    <option value="">Select category...</option>
                    {categoryList.map(c => (
                      <option key={c.id} value={c.id}>{c.parent_name? `${c.parent_name} › ` : ''}{c.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-section">
                <label className="form-label">Hero Image</label>
                <div className="add-hero-upload">
                  {addHeroPreview? (
                    <div className="add-hero-preview">
                      <img src={addHeroPreview} alt="Hero preview" />
                      <button className="btn-remove-kit" onClick={() => { setAddHeroImage(null); setAddHeroPreview(null); }}>✕</button>
                    </div>
                  ) : (
                    <label className="btn-upload-image">
                      Choose Image
                      <input type="file" accept="image/*" hidden onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          setAddHeroImage(file);
                          setAddHeroPreview(URL.createObjectURL(file));
                        }
                      }} />
                    </label>
                  )}
                </div>
              </div>

              <div className="form-section">
                <label className="form-label">Hero Video</label>
                <div className="add-hero-upload">
                  {addHeroVideoPreview? (
                    <div className="add-hero-preview">
                      <video src={addHeroVideoPreview} controls className="hero-video" />
                      <button className="btn-remove-kit" onClick={() => {
                        setAddHeroVideo(null);
                        setAddHeroVideoPreview(null);
                      }}>✕</button>
                    </div>
                  ) : (
                    <label className="btn-upload-image">
                      Choose Video
                      <input
                        type="file"
                        accept="video/mp4,video/webm"
                        hidden
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          if (!ALLOWED_VIDEO_TYPES.includes(file.type)) {
                            alert('Only.mp4 or.webm files are allowed');
                            e.target.value = '';
                            return;
                          }
                          if (file.size > MAX_VIDEO_SIZE) {
                            alert(`Video is too large. Max ${MAX_VIDEO_SIZE / 1024 / 1024}MB. File: ${(file.size / 1024 / 1024).toFixed(1)}MB`);
                            e.target.value = '';
                            return;
                          }
                          setAddHeroVideo(file);
                          setAddHeroVideoPreview(URL.createObjectURL(file));
                        }}
                      />
                    </label>
                  )}
                </div>
                <div className="upload-hint">MP4/WebM, max 20MB, 15-30s</div>
              </div>

              <div className="add-variants-section">
                <div className="section-header">
                  <label className="form-label">Variants *</label>
                  <button className="btn-add-kit" onClick={addVariantRow}>+ Add Variant</button>
                </div>
                {addVariants.map((v, idx) => (
                  <div key={idx} className="add-variant-card">
                    <div className="add-variant-header">
                      <span>Variant {idx + 1}</span>
                      {addVariants.length > 1 && (
                        <button className="btn-remove-kit" onClick={() => removeVariantRow(idx)}>✕</button>
                      )}
                    </div>
                    <div className="variant-pricing-grid">
                      <div className="pricing-field">
                        <label>SKU</label>
                        <input type="text" value={v.sku} onChange={(e) => updateAddVariant(idx, 'sku', e.target.value)} />
                      </div>
                      <div className="pricing-field">
                        <label>Attribute</label>
                        <input type="text" value={v.attribute} onChange={(e) => updateAddVariant(idx, 'attribute', e.target.value)} placeholder="e.g. Color name" />
                      </div>
                      <div className="pricing-field">
                        <label>Size</label>
                        <input type="text" value={v.size} onChange={(e) => updateAddVariant(idx, 'size', e.target.value)} placeholder="e.g. 1oz" />
                      </div>
                      <div className="pricing-field">
                        <label>Retail Price *</label>
                        <input type="number" step="0.01" value={v.retail_price} onChange={(e) => updateAddVariant(idx, 'retail_price', e.target.value)} />
                      </div>
                      <div className="pricing-field">
                        <label>Wholesale</label>
                        <input type="number" step="0.01" value={v.wholesale_price} onChange={(e) => updateAddVariant(idx, 'wholesale_price', e.target.value)} />
                      </div>
                      <div className="pricing-field">
                        <label>Cost</label>
                        <input type="number" step="0.01" value={v.cost_price} onChange={(e) => updateAddVariant(idx, 'cost_price', e.target.value)} />
                      </div>
                      <div className="pricing-field">
                        <label>Case Price</label>
                        <input type="number" step="0.01" value={v.case_price} onChange={(e) => updateAddVariant(idx, 'case_price', e.target.value)} />
                      </div>
                      <div className="pricing-field">
                        <label>Case Qty</label>
                        <input type="number" step="1" value={v.case_qty} onChange={(e) => updateAddVariant(idx, 'case_qty', e.target.value)} />
                      </div>
                      <div className="pricing-field">
                        <label>Stock</label>
                        <input type="number" step="1" value={v.inventory_qty} onChange={(e) => updateAddVariant(idx, 'inventory_qty', e.target.value)} />
                      </div>
                    </div>
                    <div className="add-variant-image-row">
                      {addVariantImages[idx]? (
                        <div className="add-variant-image-preview">
                          <img src={URL.createObjectURL(addVariantImages[idx])} alt={`Variant ${idx + 1}`} />
                          <button className="btn-remove-kit" onClick={() => setAddVariantImages(prev => { const n = {...prev }; delete n[idx]; return n; })}>✕</button>
                        </div>
                      ) : (
                        <label className="btn-upload-variant-img">
                          + Image
                          <input type="file" accept="image/*" hidden onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) setAddVariantImages(prev => ({...prev, [idx]: file }));
                          }} />
                        </label>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="modal-footer">
                <button className="btn-cancel" onClick={() => setShowAddModal(false)}>Cancel</button>
                <button className="btn-submit" onClick={submitAddProduct} disabled={addSaving}>
                  {addSaving? 'Creating...' : 'Create Product'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={bulkDeleteConfirm}
        title="Delete Multiple Products"
        message={`Are you sure you want to delete ${selectedProducts.length} product group${selectedProducts.length > 1? 's' : ''}? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
        onConfirm={confirmBulkDelete}
        onCancel={() => setBulkDeleteConfirm(false)}
      />
    </div>
  );
};

export default Products;